import React, { useState, useContext, useCallback, useMemo, useEffect } from 'react';
import { useSave, Context, useGet, useFetch } from 'store';
import moment from 'moment';

import { Card, Popconfirm, Button, notification } from 'antd';

import { Table, Modal } from '_src/components';
import { PersistentRepo } from '_src/shared/repo';
import { PageContainer } from '_src/shared/styles';

import services from '_src/services';
import { Restricted } from '_src/shared/restrict';
import { Select, Input } from '_src/shared/form-helpers';
import { FilterRow } from './styles';
import { Refresh } from '_src/components';

const stylesTablet = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  ? { marginTop: 30 }
  : { marginTop: 0 };

const resource = 'autoArchive';
const config = services[resource];
const reportsConfig = services['reports'];
const evaluationTestsConfig = services['evaluationTests'];

const attendanceConfig = services.attendance;

const ALL_OPTION = { id: 'all', label: 'All' };

const uniqueById = items => {
  const seen = new Set();
  return items.filter(({ id }) => {
    if (seen.has(id)) {
      return false;
    }

    seen.add(id);
    return true;
  });
};

const getInitialLocation = () => {
  const allBranchesLocation = PersistentRepo.get('allBranches');
  return allBranchesLocation ? allBranchesLocation : PersistentRepo.get('location', 'allxallx1');
};

const formatResponseDate = date => moment.parseZone(date).format('MM/DD/YYYY');

const buildAttendanceOptions = items =>
  uniqueById([
    ALL_OPTION,
    ...items
      .filter(item => item.sessions)
      .map(filteredDay => {
        const yardSessionsIds = filteredDay.sessions
          .filter(({ locationType }) => locationType === 1)
          .map(({ id }) => id);

        const classroomSessionsIds = filteredDay.sessions
          .filter(({ locationType }) => locationType === 2)
          .map(({ id }) => id);

        const dayHasYardSessions = filteredDay.sessions.some(
          ({ locationType }) => locationType === 1
        );

        const dayHasClassroomSessions = filteredDay.sessions.some(
          ({ locationType }) => locationType === 2
        );
        return {
          id: filteredDay.date,
          label: filteredDay.date,
          dayHasYardSessions,
          dayHasClassroomSessions,
          yardSessionsIds,
          classroomSessionsIds
        };
      })
  ]);

const buildTestingResponseOptions = items =>
  uniqueById([
    ALL_OPTION,
    ...Array.from(
      new Map(
        (items || [])
          .filter(item => item.responseDate)
          .map(({ responseDate }) => {
            const formatted = moment.parseZone(responseDate).format('MM/DD/YYYY');
            return [formatted, { id: formatted, label: formatted }];
          })
      ).values()
    )
  ]);

const createAttendanceParams = (locationValue, firstName, lastName) => {
  const [, , branchId] = locationValue.split('x');

  return {
    branchId,
    ...(firstName && { firstName }),
    ...(lastName && { lastName })
  };
};

const archiveOptionDescription = {
  all: 'All classroom sessions, yard sessions and evaluations will be archived. Student will be returned to classroom.',
  'all-class-sessions':
    'All classroom sessions will be archived. Student will stay/return to classroom. Yard data will remain.',
  'all-yard-sessions':
    'All yard sessions will be archived. Student location will not change. Class data will remain.',
  'sessions-yard': '',
  'sessions-classroom': '',
  test: ''
};

const AutoArchive = () => {
  const initialLocation = useMemo(() => getInitialLocation(), []);

  const { refreshResource } = useContext(Context);
  const { save: changeStatus } = useSave(config.changeStatus);
  const { save: editArchiveStudent, busy: archiveStudentLoading } = useSave(
    attendanceConfig.archiveStudent
  );

  const [location, setLocation] = useState(initialLocation);
  const [nameInput, setNameInput] = useState({
    firstName: '',
    lastName: ''
  });
  const [debouncedName, setDebouncedName] = useState(nameInput);

  const [loading, setLoading] = useState(false);
  const [studentIdFMCSAPopupConfirm, setStudentIdFMCSAPopupConfirm] = useState(false);
  const [showSelectDateConfirmModal, setShowSelectDateConfirmModal] = useState(false);
  const [showDoubleArchiveConfirmModal, setShowDoubleArchiveConfirmModal] = useState();
  const [typeOfArchive, setTypeOfArchive] = useState(null);
  const [selectedDateToArchive, setSelectedDateToArchive] = useState('all');
  const [allTestingResponseDays, setAllTestingResponseDays] = useState([]);
  const [allAttendanceDays, setAllAttendanceDays] = useState([]);
  const [currentStudent, setCurrentStudent] = useState({});

  const { get: fetchAllResponses } = useFetch({ ...evaluationTestsConfig.getResponses });
  const { get: fetchAllAttendance } = useFetch({ ...reportsConfig.getAttendance });

  const attendanceParams = useMemo(
    () => createAttendanceParams(location, debouncedName.firstName, debouncedName.lastName),
    [location, debouncedName]
  );

  const { data, busy: loadingGet } = useGet({ ...config.get, params: attendanceParams });
  const tableData = (data || []).filter(({ dismissed, archiveCp }) => !archiveCp && !dismissed);

  const locations = useMemo(
    () => ({
      options: [ALL_OPTION],
      ...attendanceConfig.location
    }),
    []
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(nameInput), 500);
    return () => clearTimeout(t);
  }, [nameInput]);

  const handleLocationChange = useCallback(loc => {
    if (loc === 'all') {
      PersistentRepo.set('allBranches', loc);
      PersistentRepo.delete('location');
    } else {
      PersistentRepo.delete('allBranches');
      PersistentRepo.set('location', loc);
    }

    setLocation(loc);
  }, []);

  const loadStudentArchiveData = useCallback(
    async studentId => {
      setLoading(true);
      const [responses, attendance] = await Promise.all([
        fetchAllResponses({ params: { studentId } }),
        fetchAllAttendance({ params: { studentId, reportCard: false } })
      ]);

      const attendanceOptions = buildAttendanceOptions(attendance);
      const responseOptions = buildTestingResponseOptions(responses);

      setAllAttendanceDays(attendanceOptions);
      setAllTestingResponseDays(responseOptions);
      setLoading(false);

      const hasAttendanceData = attendanceOptions.length > 1;
      const hasResponseData = responseOptions.length > 1;

      return hasAttendanceData || hasResponseData;
    },
    [fetchAllAttendance, fetchAllResponses]
  );

  const dismissStudent = useCallback(
    async studentId => {
      setLoading(true);
      try {
        const { success } = await changeStatus({ studentId, dismissed: true });
        if (success === 1) {
          notification.success({ message: 'Student has been sucessfully dismissed' });
          refreshResource('autoArchive');
        } else {
          notification.error({ message: 'Failed to merge student entries' });
        }
      } finally {
        setLoading(false);
      }
    },
    [changeStatus, refreshResource]
  );

  const archiveWithoutSelection = useCallback(
    async studentId => {
      setLoading(true);
      try {
        const { success } = await changeStatus({ studentId, archived: true });
        if (success === 1) {
          setStudentIdFMCSAPopupConfirm(null);
          notification.success({ message: 'Student has been sucessfully archived' });
          refreshResource('autoArchive');
        } else {
          notification.error({ message: 'Failed to merge student entries' });
        }
      } finally {
        setLoading(false);
      }
    },
    [changeStatus, refreshResource]
  );

  const handleArchive = useCallback(
    async (type, student, isFmcsaSubmitted = false) => {
      if (type === 'dismiss') {
        await dismissStudent(student.id);
        return;
      }

      setCurrentStudent(student);

      if (isFmcsaSubmitted) {
        setStudentIdFMCSAPopupConfirm(null);
      }

      try {
        const hasData = await loadStudentArchiveData(student.id);

        if (!hasData) {
          await archiveWithoutSelection(student.id);
          return;
        }

        setShowSelectDateConfirmModal(true);
      } catch (error) {
        setLoading(false);
        notification.error({ message: 'Unable to load student data for archiving' });
      }
    },
    [archiveWithoutSelection, dismissStudent, loadStudentArchiveData]
  );

  const archiveStudent = useCallback(
    async studentId => {
      setLoading(true);

      try {
        const basePayload = {
          id: studentId,
          archived: true,
          all: false
        };
        const archiveAllPayload = {
          id: studentId,
          archived: true,
          all: true,
          type: 'all'
        };
        const archiveAllClassroomPayload = {
          ...basePayload,
          locationType: 2,
          type: 'yard_or_classroom',
          attendanceIds: allAttendanceDays
            .map(({ classroomSessionsIds }) => {
              return classroomSessionsIds;
            })
            .flat(Infinity)
            .filter(item => item !== undefined)
        };
        const archiveAllYardPayload = {
          ...basePayload,
          locationType: 1,
          type: 'yard_or_classroom',
          attendanceIds: allAttendanceDays
            .map(({ yardSessionsIds }) => {
              return yardSessionsIds;
            })
            .flat(Infinity)
            .filter(item => item !== undefined),
          testsDate: [
            ...allTestingResponseDays
              .filter(({ type, id }) => type !== 4 && id !== 'all')
              .map(({ id }) => {
                return id;
              })
          ]
        };
        const archiveSessionYardPayload = {
          ...basePayload,
          type: 'sessions',
          location_type: 1,
          attendanceIds:
            allAttendanceDays.find(({ id }) => id === selectedDateToArchive)?.yardSessionsIds || [],
          ...(currentStudent.yardOnly && { yardOnly: true })
        };
        const archiveSessionClassroomPayload = {
          ...basePayload,
          type: 'sessions',
          location_type: 2,
          attendanceIds:
            allAttendanceDays.find(({ id }) => id === selectedDateToArchive)
              ?.classroomSessionsIds || []
        };
        const archiveAllTestsPayload = {
          ...basePayload,
          type: 'test',
          testsDate: allTestingResponseDays
            .filter(({ id, type }) => id !== 'all' && type !== 4)
            .map(({ id }) => {
              return id;
            })
        };
        const archiveSingleTestPayload = {
          ...basePayload,
          type: 'test',
          testsDate: [selectedDateToArchive]
        };

        let payload = {};

        switch (typeOfArchive) {
          case 'all':
            payload = archiveAllPayload;
            break;
          case 'all-class-sessions':
            payload = archiveAllClassroomPayload;
            break;
          case 'all-yard-sessions':
            payload = archiveAllYardPayload;
            break;
          case 'sessions-yard':
            payload = archiveSessionYardPayload;
            break;
          case 'sessions-classroom':
            payload = archiveSessionClassroomPayload;
            break;
          case 'test':
            if (selectedDateToArchive === 'all') {
              payload = archiveAllTestsPayload;
            } else {
              payload = archiveSingleTestPayload;
            }

            break;
          default:
            payload = archiveAllPayload;
        }

        if (payload.attendanceIds && !payload.attendanceIds.length) {
          notification.warning({
            message: `There are no dates to archive for this option`,
            duration: 5
          });

          return;
        }

        if (
          payload.attendanceIds &&
          payload.testsDate &&
          !payload.attendanceIds.length &&
          !payload.attendanceIds.testsDate
        ) {
          notification.warning({
            message: `There are no dates to archive for this option`,
            duration: 5
          });

          return;
        }

        const archiveResponse = await editArchiveStudent(payload);

        if (archiveResponse.success !== 1) {
          notification.error({ message: 'Something went wrong', duration: 5 });
          return;
        }

        const statusResponse = await changeStatus({ studentId, archived: true });
        if (statusResponse.success !== 1) {
          notification.error({ message: 'Something went wrong', duration: 5 });
          return;
        }

        refreshResource('autoArchive');
        setShowDoubleArchiveConfirmModal(false);

        setShowSelectDateConfirmModal(false);
        setSelectedDateToArchive('all');
        setTypeOfArchive('all');
        notification.success({ message: 'Successfully archived student', duration: 5 });
      } finally {
        setLoading(false);
      }
    },
    [
      changeStatus,
      currentStudent,
      editArchiveStudent,
      refreshResource,
      selectedDateToArchive,
      typeOfArchive
    ]
  );

  const tableColumns = useMemo(
    () => [
      ...config.table,
      {
        id: 'submit',
        title: 'Actions',
        render: (_, student) => {
          const isFmcsaSubmitted =
            (student.locationType === 1 && student.yardSubmittedToFmsca) ||
            (student.locationType === 2 && student.classSubmittedToFmsca);

          return (
            <div style={{ display: 'flex', columnGap: '20px' }}>
              <Popconfirm
                title="Are you sure?"
                okType="primary"
                okText="Yes"
                onConfirm={() =>
                  isFmcsaSubmitted
                    ? setStudentIdFMCSAPopupConfirm(student.id)
                    : handleArchive('archive', student)
                }
              >
                <Popconfirm
                  title=<div style={{ color: '#3b3b3b', fontWeight: 500 }}>
                    This student has been submitted to FMCSA and cannot be submitted again.
                    <br /> Would you like to continue archiving ?
                  </div>
                  okType="primary"
                  okText="Yes"
                  visible={student.id === studentIdFMCSAPopupConfirm}
                  onConfirm={() => handleArchive('archive', student, true)}
                  onCancel={() => setStudentIdFMCSAPopupConfirm(null)}
                >
                  <Button type="primary">Archive</Button>
                </Popconfirm>
              </Popconfirm>

              <Popconfirm
                title="Are you sure?"
                okType="primary"
                okText="Yes"
                onConfirm={() => handleArchive('dismiss', student)}
              >
                <Button type="danger">Dismiss</Button>
              </Popconfirm>
            </div>
          );
        }
      }
    ],
    [config.table, handleArchive, studentIdFMCSAPopupConfirm]
  );

  const renderDoubleArchiveCancel = () => (
    <Button
      key="cancel"
      onClick={() => {
        showDoubleArchiveConfirmModal
          ? setShowDoubleArchiveConfirmModal(false)
          : setShowSelectDateConfirmModal(false);
      }}
    >
      Cancel
    </Button>
  );

  const renderArchiveConfirmButton = () => (
    <Button key="confirmArchive" type="primary" onClick={() => setShowSelectDateConfirmModal(true)}>
      Archive
    </Button>
  );

  const renderDoubleArchiveConfirmButton = (studentId, disable) => {
    const isValidSelectedDate = !typeOfArchive?.includes('all') && !Boolean(selectedDateToArchive);

    return (
      <Button
        key="confirmArchive"
        type="primary"
        disabled={disable || !Boolean(typeOfArchive) || isValidSelectedDate}
        loading={archiveStudentLoading}
        onClick={() => {
          if (selectedDateToArchive === 'all') {
            setShowDoubleArchiveConfirmModal(true);
            archiveStudent(studentId);
            return;
          }
          archiveStudent(studentId);
        }}
      >
        Archive
      </Button>
    );
  };

  const archiveDates =
    typeOfArchive === 'test'
      ? allTestingResponseDays.filter(({ type }) => type !== 4)
      : allAttendanceDays.filter(item => {
          if (typeOfArchive === 'sessions-yard') {
            return item.dayHasYardSessions;
          }

          if (typeOfArchive === 'sessions-classroom') {
            return item.dayHasClassroomSessions;
          }

          return item;
        });

  const disableSelectArchiveDates =
    archiveDates.length === 0 || (archiveDates.length === 1 && archiveDates[0]?.id === 'all');

  return (
    <PageContainer style={stylesTablet}>
      <Refresh />
      <Card title="Archive Program Change">
        <FilterRow>
          <Select
            showSearch
            onChange={handleLocationChange}
            {...locations}
            value={location}
            label="Branch"
            style={{ width: '250px' }}
          />
          <Input
            label="Search by First Name"
            onChange={e => setNameInput(v => ({ ...v, firstName: e }))}
            value={nameInput.firstName}
            allowClear
          />
          <Input
            label="Search by Last Name"
            value={nameInput.lastName}
            onChange={e => setNameInput(v => ({ ...v, lastName: e }))}
            allowClear
          />
        </FilterRow>

        <Table
          locale={{
            emptyText: (
              <div style={{ fontSize: '25px', margin: '5vw 0' }}>No students available.</div>
            )
          }}
          columns={tableColumns}
          data={tableData}
          loading={loadingGet || loading}
          onClick={() => {}}
        />
      </Card>

      {showSelectDateConfirmModal && (
        <Modal
          className="moveToYard"
          title="Please select a date to archive student data"
          visible={showSelectDateConfirmModal}
          onCancel={() => setShowSelectDateConfirmModal(false)}
          footer={[
            renderDoubleArchiveCancel(),
            renderDoubleArchiveConfirmButton(currentStudent.id, disableSelectArchiveDates)
          ]}
        >
          <Select
            label="Select type"
            placeholder="Type"
            isGrouped
            options={[
              {
                id: 'all',
                label: 'All',
                items: [
                  { id: 'all', label: 'All (Archive class and yard evals and sessions)' },
                  { id: 'all-class-sessions', label: 'Class (Archive class sessions)' },
                  {
                    id: 'all-yard-sessions',
                    label: 'Yard (Archive yard evals and sessions)'
                  }
                ]
              },
              {
                id: 'sessions',
                label: 'Sessions',
                items: [
                  { id: 'sessions-yard', label: 'Sessions-Yard' },
                  { id: 'sessions-classroom', label: 'Sessions-Classroom' }
                ]
              },
              {
                id: 'tests',
                label: 'Evaluations',
                items: [{ id: 'test', label: 'Evaluations' }]
              }
            ]}
            value={typeOfArchive || undefined}
            onChange={value => {
              setTypeOfArchive(value);
              setSelectedDateToArchive(null);
            }}
            style={{ width: '350px', marginBottom: '10px' }}
          />

          {archiveOptionDescription[typeOfArchive] && (
            <div
              style={{
                backgroundColor: '#ebaf2a66',
                padding: '5px 8px',
                borderRadius: '8px',
                marginBottom: '20px',
                width: '350px'
              }}
            >
              {archiveOptionDescription[typeOfArchive]}
            </div>
          )}
          <Select
            label="Select a date"
            disabled={
              !Boolean(typeOfArchive) || typeOfArchive.includes('all') || disableSelectArchiveDates
            }
            showSearch
            onChange={value => setSelectedDateToArchive(value)}
            value={selectedDateToArchive}
            options={archiveDates}
            style={{ width: '350px' }}
          />

          {disableSelectArchiveDates && (
            <div style={{ color: '#ff3900d1', fontWeight: 500, marginTop: '10px' }}>
              *No available dates to archive
            </div>
          )}
        </Modal>
      )}
    </PageContainer>
  );
};

export default AutoArchive;
